import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GeneralSettings from "./settings/GeneralSettings";
import AccountSettings from "./settings/AccountSettings";
import { UnsavedChangesContext, createUnsavedRegistry } from "./settings/unsavedChanges";
import { useTranslation } from "../i18n/LanguageContext";

function SettingsNavIcon({ src, active }) {
  if (src === "settings") {
    return (
      <SettingsOutlinedIcon
        sx={{ fontSize: 18, color: active ? "#1a1a1a" : "#9ca3af" }}
      />
    );
  }

  if (src === "account") {
    return (
      <PersonOutlineIcon
        sx={{ fontSize: 18, color: active ? "#1a1a1a" : "#9ca3af" }}
      />
    );
  }

  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 18,
        height: 18,
        display: "inline-block",
        flexShrink: 0,
        backgroundColor: active ? "#1a1a1a" : "#9ca3af",
        mask: `url(${src}) center / contain no-repeat`,
        WebkitMask: `url(${src}) center / contain no-repeat`,
      }}
    />
  );
}

function NavButton({ item, active, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        py: 0.875,
        borderRadius: 1.5,
        cursor: "pointer",
        bgcolor: active ? "#FFFFFF" : "transparent",
        color: active ? "#1a1a1a" : "#6b7280",
        boxShadow: active ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
        transition: "background-color 0.12s, color 0.12s",
        "&:hover": {
          bgcolor: active ? "#FFFFFF" : "#E4E4E4",
        },
      }}
    >
      <SettingsNavIcon src={item.icon} active={active} />
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: active ? 500 : 400,
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
}

export default function SettingsDialog({ open, onClose, initialSection = "general" }) {
  const { t } = useTranslation();
  const [section, setSection] = useState(initialSection);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Unsaved-changes guard: sections register { dirty, save } here (see
  // settings/unsavedChanges.js). Closing the dialog or switching tabs while
  // anything is dirty asks first — pendingAction is what to do once the user
  // picks save or discard.
  const registry = useMemo(() => createUnsavedRegistry(), []);
  const [pendingAction, setPendingAction] = useState(null);
  const [guardSaving, setGuardSaving] = useState(false);
  const [guardError, setGuardError] = useState("");

  const guarded = (action) => {
    if (registry.dirtyEntries().length > 0) {
      setGuardError("");
      setPendingAction(() => action);
    } else {
      action();
    }
  };
  const requestClose = () => guarded(onClose);
  const requestSection = (id) => {
    if (id === section) return;
    guarded(() => setSection(id));
  };

  const handleGuardDiscard = () => {
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  };

  const handleGuardSave = async () => {
    setGuardSaving(true);
    setGuardError("");
    const results = [];
    for (const entry of registry.dirtyEntries()) {
      results.push(await entry.save());
    }
    setGuardSaving(false);
    if (results.every(Boolean)) {
      const action = pendingAction;
      setPendingAction(null);
      action?.();
    } else {
      setGuardError(t("settings.unsaved.saveError"));
    }
  };

  const navGroups = [
    {
      label: t("settings.dialog.groupLabel"),
      items: [
        { id: "general", label: t("settings.dialog.general"), icon: "settings" },
        { id: "account", label: t("settings.dialog.account"), icon: "account" },
      ],
    },
  ];
  const activeItem = navGroups.flatMap((group) => group.items).find((item) => item.id === section);

  React.useEffect(() => {
    if (open) setSection(initialSection);
  }, [open, initialSection]);

  // Shared grouped nav list — used both as the always-visible desktop left
  // column and inside the mobile full-height Drawer, so the two never drift.
  const navList = (
    <Box sx={{ px: 1.25, py: 1.5 }}>
      {navGroups.map((group, groupIndex) => (
        <Box key={group.label} sx={{ mb: groupIndex < navGroups.length - 1 ? 2 : 0 }}>
          <Typography
            sx={{
              px: 1.25,
              mb: 0.75,
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#9ca3af",
            }}
          >
            {group.label}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {group.items.map((item) => (
              <NavButton
                key={item.id}
                item={item}
                active={section === item.id}
                onClick={() => {
                  setDrawerOpen(false);
                  requestSection(item.id);
                }}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <UnsavedChangesContext.Provider value={registry}>
    <Dialog
      open={open}
      onClose={requestClose}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: "min(1080px, 94vw)" },
          height: { xs: "100vh", sm: "min(820px, 88vh)" },
          maxWidth: "none",
          maxHeight: "none",
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
          border: { xs: "none", sm: "1px solid #e5e7eb" },
          boxShadow: "0 24px 64px rgba(16,24,40,0.18), 0 4px 12px rgba(16,24,40,0.08)",
        },
      }}
    >
      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          {/* Mobile header: hamburger (opens a menu to switch sections) on the
              left, close on the right — same left-menu/right-close pattern as
              the site's mobile top bar. */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1.25,
              py: 0.75,
              borderBottom: "1px solid #e5e7eb",
              bgcolor: "#F1F1F1",
            }}
          >
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Open settings menu"
              size="small"
              sx={{
                bgcolor: "#FFFFFF",
                border: "1px solid #e5e7eb",
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            >
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#1a1a1a" }}>
              {activeItem?.label}
            </Typography>

            <IconButton
              onClick={requestClose}
              aria-label="Close settings"
              size="small"
              sx={{
                bgcolor: "#FFFFFF",
                border: "1px solid #e5e7eb",
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            // Stack above the SettingsDialog itself — both are separate MUI
            // Modal portals at the same default z-index, and without this,
            // the (aria-hidden) Dialog can still paint/intercept pointer
            // events on top of the nested Drawer.
            sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
            PaperProps={{ sx: { width: 240, bgcolor: "#EBEBEB" } }}
          >
            {navList}
          </Drawer>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", bgcolor: "#F1F1F1" }}>
            <Box sx={{ px: 2, pb: 2, pt: 2 }}>
              {section === "general" && <GeneralSettings />}
              {section === "account" && <AccountSettings onClose={onClose} />}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", height: "100%", minHeight: 0 }}>
          {/* Left nav — full height, scrollable, grouped */}
          <Box
            sx={{
              width: 220,
              flexShrink: 0,
              bgcolor: "#EBEBEB",
              borderRight: "1px solid #e5e7eb",
              overflowY: "auto",
            }}
          >
            {navList}
          </Box>

          {/* Right column: static header + scrollable content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "#F1F1F1",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                px: 1.25,
                py: 0.75,
                bgcolor: "#F1F1F1",
              }}
            >
              <IconButton
                onClick={requestClose}
                aria-label="Close settings"
                size="small"
                sx={{
                  bgcolor: "#FFFFFF",
                  border: "1px solid #e5e7eb",
                  "&:hover": { bgcolor: "#F9FAFB" },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              <Box sx={{ px: 2, pb: 2, pt: 2 }}>
                {section === "general" && <GeneralSettings />}
                {section === "account" && <AccountSettings onClose={onClose} />}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>

    <Dialog
      open={Boolean(pendingAction)}
      onClose={() => !guardSaving && setPendingAction(null)}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: (th) => th.zIndex.modal + 2 }}
    >
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600 }}>{t("settings.unsaved.title")}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.8125rem", color: "#4b5563", lineHeight: 1.5 }}>
          {t("settings.unsaved.body")}
        </Typography>
        {guardError && (
          <Typography sx={{ fontSize: "0.78125rem", color: "#b42318", mt: 1.5 }}>{guardError}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap" }}>
        <Button
          onClick={handleGuardDiscard}
          disabled={guardSaving}
          sx={{ ...guardButtonSx, color: "#b42318", mr: "auto", "&:hover": { bgcolor: "#FEF3F2" } }}
        >
          {t("settings.unsaved.discard")}
        </Button>
        <Button onClick={() => setPendingAction(null)} disabled={guardSaving} sx={{ ...guardButtonSx, color: "#4b5563" }}>
          {t("settings.unsaved.keepEditing")}
        </Button>
        <Button
          variant="contained"
          onClick={handleGuardSave}
          disabled={guardSaving}
          sx={{ ...guardButtonSx, color: "#1a1a1a", bgcolor: "#EBEBEB", "&:hover": { bgcolor: "#E0E0E0", boxShadow: "none" } }}
        >
          {guardSaving ? <CircularProgress size={16} sx={{ color: "#1a1a1a" }} /> : t("settings.unsaved.save")}
        </Button>
      </DialogActions>
    </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

const guardButtonSx = {
  textTransform: "none",
  borderRadius: 1.5,
  px: 2,
  py: 0.5,
  fontSize: "0.8125rem",
  boxShadow: "none",
};
